import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Cpu, HardDrive, Database, Users, Server, Zap } from 'lucide-react';

const CLOUD_PLANS = [
  {
    cpu: '2 núcleos',
    ram: '3 GB',
    storage: '100 GB NVMe',
    inodes: '2 000 000',
    phpWorkers: '100',
    sites: '100',
    price: 'R$ 149,90',
    oldPrice: 'R$ 299,90',
    savings: 'ECONOMIZE 50%'
  },
  {
    cpu: '4 núcleos',
    ram: '6 GB',
    storage: '200 GB NVMe',
    inodes: '3 000 000',
    phpWorkers: '200',
    sites: '100',
    price: 'R$ 249,90',
    oldPrice: 'R$ 499,90',
    savings: 'ECONOMIZE 50%'
  },
  {
    cpu: '6 núcleos',
    ram: '12 GB',
    storage: '300 GB NVMe',
    inodes: '4 000 000',
    phpWorkers: '300',
    sites: '100',
    price: 'R$ 399,90',
    oldPrice: 'R$ 799,90',
    savings: 'ECONOMIZE 50%'
  }
];

const BENEFITS = [
  {
    icon: <Server size={28} className="text-purple-400" />, title: 'Recursos Dedicados', desc: 'Performance garantida com recursos exclusivos para seu projeto.'
  },
  {
    icon: <Zap size={28} className="text-purple-400" />, title: 'Alta Performance', desc: 'Armazenamento NVMe ultrarrápido e PHP Workers dedicados.'
  },
  {
    icon: <Database size={28} className="text-purple-400" />, title: 'Escalabilidade', desc: 'Cresça conforme sua demanda, sem limites para seus sites.'
  },
  {
    icon: <Users size={28} className="text-purple-400" />, title: 'Multi-sites', desc: 'Hospede até 100 sites em um único plano.'
  },
  {
    icon: <Cpu size={28} className="text-purple-400" />, title: 'Ambiente Gerenciado', desc: 'Atualizações, monitoramento e suporte especializado 24/7.'
  },
  {
    icon: <HardDrive size={28} className="text-purple-400" />, title: 'Backups Diários', desc: 'Segurança e tranquilidade com backups automáticos.'
  },
];

export default function CloudHostingLandingPage() {
  const [selected, setSelected] = useState(1);
  const plan = CLOUD_PLANS[selected];
  return (
    <div className="bg-gradient-to-br from-gray-950 via-purple-950 to-gray-900 min-h-screen flex flex-col">
      <Header />
      {/* HERO */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 text-center relative z-10">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900/40 via-transparent to-indigo-900/30 pointer-events-none" />
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">Hospedagem Cloud Gerenciada</h1>
        <p className="text-lg md:text-2xl text-gray-300 max-w-2xl mx-auto mb-8">A potência do Cloud com a simplicidade do gerenciamento Hostul.<br className="hidden md:block" />
        Recursos dedicados, alta performance e escalabilidade para projetos exigentes.</p>
        <div className="flex flex-col md:flex-row gap-4 justify-center items-center mt-8">
          <a href="#planos-cloud" className="px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 text-lg transition-all">Ver Planos</a>
          <a href="#beneficios-cloud" className="px-8 py-4 rounded-xl font-bold text-purple-400 border-2 border-purple-600 bg-transparent hover:bg-purple-900/10 hover:text-white transition-all text-lg">Por que escolher?</a>
        </div>
      </section>
      {/* BENEFÍCIOS */}
      <section id="beneficios-cloud" className="py-12 md:py-20">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-10">Por que escolher nossa Hospedagem Cloud?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-gray-900/80 rounded-2xl p-6 flex flex-col items-center text-center shadow-lg border border-purple-900">
                <div className="mb-3">{b.icon}</div>
                <h3 className="text-lg font-bold text-purple-200 mb-2">{b.title}</h3>
                <p className="text-gray-300 text-sm">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* PLANOS SLIDER */}
      <section id="planos-cloud" className="py-12 md:py-20 bg-gradient-to-b from-gray-950/80 to-transparent">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-10">Escolha sua configuração ideal</h2>
          <div className="max-w-xl mx-auto bg-gray-900/80 rounded-2xl shadow-xl border border-purple-900 p-8 flex flex-col items-center">
            <div className="w-full mb-8">
              <input
                type="range"
                min="0"
                max="2"
                step="1"
                value={selected}
                onChange={e => setSelected(Number(e.target.value))}
                className="w-full accent-purple-600 h-2 rounded-lg appearance-none bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-400"
                style={{ accentColor: '#a78bfa' }}
              />
              <div className="flex justify-between mt-2 text-xs text-purple-200 font-semibold">
                <span>2 núcleos</span>
                <span>4 núcleos</span>
                <span>6 núcleos</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 w-full mb-6">
              <div className="text-gray-300 font-medium flex items-center"><Cpu size={18} className="mr-2 text-purple-400" />{plan.cpu}</div>
              <div className="text-gray-300 font-medium flex items-center"><Users size={18} className="mr-2 text-purple-400" />{plan.sites} sites</div>
              <div className="text-gray-300 font-medium flex items-center"><Zap size={18} className="mr-2 text-purple-400" />{plan.phpWorkers} PHP Workers</div>
              <div className="text-gray-300 font-medium flex items-center"><Database size={18} className="mr-2 text-purple-400" />{plan.inodes} inodes</div>
              <div className="text-gray-300 font-medium flex items-center"><HardDrive size={18} className="mr-2 text-purple-400" />{plan.storage}</div>
              <div className="text-gray-300 font-medium flex items-center"><span className="mr-2" />{plan.ram} RAM</div>
            </div>
            <div className="flex flex-col items-start w-full mb-4">
              <div className="flex items-center mb-1">
                <span className="text-base md:text-lg text-gray-400 line-through mr-2 select-none">{plan.oldPrice}</span>
                <span className="px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-600 to-indigo-500 text-white text-xs font-bold shadow-sm border border-purple-400/40 tracking-wide uppercase" style={{letterSpacing: '0.04em'}}>{plan.savings}</span>
              </div>
              <span className="text-3xl md:text-4xl font-bold text-purple-300 leading-tight flex items-baseline">
                <span className="text-base md:text-lg text-purple-200 mr-0.5">R$</span>{plan.price.replace('R$ ', '')}<span className="text-xs md:text-sm text-gray-400 ml-0.5 font-normal">/mês</span>
              </span>
              <span className="text-[11px] md:text-xs text-gray-400 font-normal mt-1 mb-2 block">Preço promocional para 12 meses.</span>
            </div>
            <button className="w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-300 bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-400 text-base flex justify-center items-center mb-0 mt-1">Quero este plano</button>
          </div>
        </div>
      </section>
      {/* CTA FINAL */}
      <section className="py-12 md:py-20 text-center">
        <div className="container mx-auto px-2 md:px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">Pronto para turbinar seu projeto na nuvem?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">Hospede seus sites com máxima performance, segurança e suporte especializado Hostul.</p>
          <a href="#planos-cloud" className="inline-block px-8 py-4 rounded-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:from-purple-700 hover:to-indigo-700 text-lg transition-all">Quero minha Hospedagem Cloud</a>
        </div>
      </section>
      <Footer />
    </div>
  );
} 